import { useMoneyFormatter } from "@/lib/money-format";
import { useEffect, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Loader2, Pencil, RefreshCw, Send, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useDiscardSupportDocument,
  useEmitSupportDocument,
  useRefreshSupportDocumentStatus,
  useSupportDocument,
} from "@/lib/api/support-documents.queries";
import { translateSupportDocError } from "@/components/support-documents/error-translations";
import { SupportDocForm } from "@/components/support-documents/SupportDocForm";
import { DiscardDraftDialog } from "@/components/shared/DiscardDraftDialog";
import { useTeamPermission } from "@/lib/team-context";
import { STATUS_BADGE_VARIANT } from "@/components/support-documents/format";
import { SupportDocArtifactsPanel } from "@/components/support-documents/SupportDocArtifactsPanel";
import { SupportDocCudsCard } from "@/components/support-documents/SupportDocCudsCard";
import { SupportDocStatusTimeline } from "@/components/support-documents/SupportDocStatusTimeline";
import { DocumentJournalPanel } from "@/components/journal/DocumentJournalPanel";
import { DianReadinessBanner } from "@/components/companies/DianReadinessBanner";
import {
  useCompanyDianReadiness,
  useDianReadinessReason,
} from "@/lib/dian/use-company-dian-readiness";
import { useActiveCompany } from "@/lib/company-context";
import { showEmitErrorToast } from "@/lib/dian/emit-error-toast";

export const Route = createFileRoute(
  "/companies/$companyId/documentos-soporte/$documentId",
)({
  component: SupportDocDetailPage,
});

function SupportDocDetailPage() {
  return (
    <RequireAuth>
      <AppShell>
        <SupportDocDetailContent />
      </AppShell>
    </RequireAuth>
  );
}

function SupportDocDetailContent() {
  const formatCop = useMoneyFormatter();
  const { t } = useTranslation();
  const { companyId, documentId } = Route.useParams();
  const navigate = useNavigate();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const docQuery = useSupportDocument(companyId, documentId);
  const refresh = useRefreshSupportDocumentStatus(companyId);
  const emit = useEmitSupportDocument(companyId);
  const discard = useDiscardSupportDocument(companyId);
  const canWrite = useTeamPermission("support_documents.write");
  const [editing, setEditing] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const readiness = useCompanyDianReadiness(companyId, "documento_soporte");
  const readinessReason = useDianReadinessReason(readiness, t);

  if (docQuery.isPending) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (docQuery.isError || !docQuery.data) {
    return (
      <ErrorState
        title={t("supportDocs.detail.error_title")}
        description={t("supportDocs.detail.error_body")}
        onRetry={() => void docQuery.refetch()}
        retryLabel={t("common.retry")}
      />
    );
  }

  const doc = docQuery.data;
  const canRefresh = !!doc.processId;
  const canEmit = doc.status === "draft" || doc.status === "failed";
  // A draft, or a document DIAN confirmed as rejected, can be reopened and corrected (PF-463).
  const canEdit = canWrite && doc.editable;
  const canDiscard = canWrite && doc.status === "draft" && doc.documentNumber === 0;
  const artifactsEnabled = doc.status === "emitted" || doc.status === "failed";

  async function onRefresh() {
    try {
      await refresh.mutateAsync({ documentRowId: documentId });
      toast.success(t("supportDocs.detail.refresh_done"));
    } catch (err) {
      toast.error(translateSupportDocError(err, t));
    }
  }

  async function onDiscard() {
    try {
      await discard.mutateAsync({ documentRowId: documentId, expectedRevision: doc.revision });
      toast.success(t("supportDocs.detail.discarded"));
      setDiscardOpen(false);
      void navigate({ to: "/companies/$companyId/documentos-soporte", params: { companyId } });
    } catch (err) {
      toast.error(translateSupportDocError(err, t));
      setDiscardOpen(false);
    }
  }

  async function onEmit() {
    try {
      await emit.mutateAsync({ documentRowId: documentId });
      toast.success(t("supportDocs.detail.emit_queued"));
    } catch (err) {
      showEmitErrorToast(err, translateSupportDocError(err, t), t, () =>
        void navigate({
          to: "/companies/$companyId/resoluciones",
          params: { companyId },
        }),
      );
    }
  }

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <DianReadinessBanner companyId={companyId} documentKind="documento_soporte" />
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/companies/$companyId/documentos-soporte"
            params={{ companyId }}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3 w-3" />
            {t("supportDocs.detail.back")}
          </Link>
          <div className="mt-1 flex items-center gap-2">
            <h1 className="font-mono text-2xl font-semibold tracking-tight">
              {doc.documentId}
            </h1>
            <Badge
              variant={STATUS_BADGE_VARIANT[doc.status]}
              className="text-[10px] uppercase"
            >
              {t(`supportDocs.status.${doc.status}`)}
            </Badge>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {doc.supplierLegalName} - {formatCop(doc.payableAmount)}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canRefresh ? (
            <Button
              variant="outline"
              onClick={() => void onRefresh()}
              disabled={refresh.isPending}
            >
              <RefreshCw
                className={
                  refresh.isPending ? "h-4 w-4 animate-spin" : "h-4 w-4"
                }
              />
              {t("supportDocs.detail.refresh_status")}
            </Button>
          ) : null}
          {canEdit && !editing ? (
            <Button variant="outline" onClick={() => setEditing(true)}>
              <Pencil className="h-4 w-4" />
              {t("supportDocs.detail.edit")}
            </Button>
          ) : null}
          {canDiscard && !editing ? (
            <Button variant="outline" onClick={() => setDiscardOpen(true)} disabled={discard.isPending}>
              <Trash2 className="h-4 w-4" />
              {t("supportDocs.detail.discard")}
            </Button>
          ) : null}
          {canEmit && !editing ? (
            <Button
              onClick={() => void onEmit()}
              disabled={emit.isPending || !readiness.ready}
              title={!readiness.ready ? readinessReason : undefined}
            >
              <Send className="h-4 w-4" />
              {t("supportDocs.detail.emit_now")}
            </Button>
          ) : null}
        </div>
      </div>

      {editing ? (
        <SupportDocForm
          companyId={companyId}
          initialDocument={doc}
          onSaved={() => setEditing(false)}
          onCancel={() => setEditing(false)}
        />
      ) : null}

      <Tabs defaultValue="overview" className={editing ? "hidden" : "w-full"}>
        <TabsList>
          <TabsTrigger value="overview">
            {t("supportDocs.detail.tab.overview")}
          </TabsTrigger>
          <TabsTrigger value="lines">
            {t("supportDocs.detail.tab.lines")}
          </TabsTrigger>
          <TabsTrigger value="dian">
            {t("supportDocs.detail.tab.dian")}
          </TabsTrigger>
        </TabsList>

        <TabsContent
          value="overview"
          className="mt-4 grid gap-4 md:grid-cols-2"
        >
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t("supportDocs.detail.summary.title")}
              </CardTitle>
              <CardDescription>
                {t("supportDocs.detail.summary.subtitle")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <Row
                label={t("supportDocs.detail.field.supplier")}
                value={doc.supplierLegalName ?? "-"}
              />
              <Row
                label={t("supportDocs.detail.field.identification")}
                value={doc.supplierIdentificationNumber ?? "-"}
              />
              <Row
                label={t("supportDocs.detail.field.issue")}
                value={doc.issueDateTime}
              />
              <Row
                label={t("supportDocs.detail.field.due")}
                value={doc.paymentDueDate ?? "-"}
              />
              <Row
                label={t("supportDocs.detail.field.payment_means")}
                value={t(`paymentMeans.${doc.paymentMeansCode}`, { defaultValue: doc.paymentMeansCode })}
              />
              <Row
                label={t("supportDocs.detail.field.document_type")}
                value={t(`supportDocs.docType.${doc.supportDocumentTypeCode}`)}
              />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t("supportDocs.totals.title")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1 text-sm">
              <Row
                label={t("supportDocs.totals.subtotal")}
                value={formatCop(doc.lineExtensionAmount)}
              />
              <Row
                label={t("supportDocs.totals.allowance")}
                value={`- ${formatCop(doc.allowanceTotalAmount)}`}
              />
              <Row
                label={t("supportDocs.totals.charge")}
                value={`+ ${formatCop(doc.chargeTotalAmount)}`}
              />
              <Row
                label={t("supportDocs.totals.tax_exclusive")}
                value={formatCop(doc.taxExclusiveAmount)}
                bold
              />
              {doc.ivaAmount > 0 ? (
                <Row
                  label={t("supportDocs.totals.iva")}
                  value={formatCop(doc.ivaAmount)}
                />
              ) : null}
              {doc.icAmount > 0 ? (
                <Row
                  label={t("supportDocs.totals.ic")}
                  value={formatCop(doc.icAmount)}
                />
              ) : null}
              {doc.icaAmount > 0 ? (
                <Row
                  label={t("supportDocs.totals.ica")}
                  value={formatCop(doc.icaAmount)}
                />
              ) : null}
              {doc.incAmount > 0 ? (
                <Row
                  label={t("supportDocs.totals.inc")}
                  value={formatCop(doc.incAmount)}
                />
              ) : null}
              <Row
                label={t("supportDocs.totals.prepaid")}
                value={`- ${formatCop(doc.prepaidAmount)}`}
              />
              <div className="border-t pt-2">
                <Row
                  label={t("supportDocs.totals.payable")}
                  value={formatCop(doc.payableAmount)}
                  bold
                />
              </div>
              <div className="mt-2 rounded-md border border-amber-200 bg-amber-50/60 p-2 dark:border-amber-900/40 dark:bg-amber-900/10">
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-amber-800 dark:text-amber-300">
                  {t("supportDocs.totals.withholdings_section")}
                </p>
                {doc.retefuenteAmount > 0 ? (
                  <Row
                    label={t("supportDocs.totals.retefuente")}
                    value={`- ${formatCop(doc.retefuenteAmount)}`}
                  />
                ) : null}
                {doc.reteivaAmount > 0 ? (
                  <Row
                    label={t("supportDocs.totals.reteiva")}
                    value={`- ${formatCop(doc.reteivaAmount)}`}
                  />
                ) : null}
                {doc.reteicaAmount > 0 ? (
                  <Row
                    label={t("supportDocs.totals.reteica")}
                    value={`- ${formatCop(doc.reteicaAmount)}`}
                  />
                ) : null}
                <Row
                  label={t("supportDocs.totals.withholding_total")}
                  value={`- ${formatCop(doc.withholdingTotalAmount)}`}
                  bold
                />
              </div>
              <div className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-900/40 dark:bg-emerald-900/20">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-800 dark:text-emerald-300">
                  {t("supportDocs.totals.net_payable_label")}
                </p>
                <p className="mt-1 font-mono text-base font-semibold text-emerald-900 dark:text-emerald-100">
                  {formatCop(doc.netPayableToSupplier)}
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="lines" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>{t("supportDocs.line.description")}</TableHead>
                    <TableHead className="text-right">
                      {t("supportDocs.line.quantity")}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("supportDocs.line.unit_price")}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("supportDocs.line.subtotal")}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("supportDocs.line.withholdings")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {doc.lines.map((line) => (
                    <TableRow key={line.lineId}>
                      <TableCell className="text-xs text-muted-foreground">
                        {line.lineId}
                      </TableCell>
                      <TableCell>
                        <p className="text-sm font-medium">
                          {line.description}
                        </p>
                        {line.sku ? (
                          <p className="font-mono text-[10px] text-muted-foreground">
                            SKU {line.sku}
                          </p>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {line.quantity} {line.unitCode}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {formatCop(line.unitPrice)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {formatCop(line.lineExtensionAmount)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {formatCop(
                          line.retefuenteAmount +
                            line.reteivaAmount +
                            line.reteicaAmount,
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="dian" className="mt-4 grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t("supportDocs.detail.timeline_title")}
              </CardTitle>
              <CardDescription>
                {t("supportDocs.detail.timeline_subtitle")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SupportDocStatusTimeline document={doc} />
            </CardContent>
          </Card>
          <div className="space-y-4">
            <SupportDocCudsCard document={doc} />
            <SupportDocArtifactsPanel
              companyId={companyId}
              documentRowId={documentId}
              enabled={artifactsEnabled}
            />
            {doc.dianStatusDescription ? (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">
                    {t("supportDocs.detail.dian_status_title")}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <Row
                    label={t("supportDocs.detail.field.dian_status")}
                    value={doc.dianStatus ?? "-"}
                  />
                  <p className="text-xs text-muted-foreground">
                    {doc.dianStatusDescription}
                  </p>
                </CardContent>
              </Card>
            ) : null}
          </div>
        </TabsContent>
      </Tabs>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("journal.document.title")}
          </CardTitle>
          <CardDescription>{t("journal.document.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent>
          <DocumentJournalPanel
            companyId={companyId}
            sourceTable="support_documents"
            documentId={documentId}
          />
        </CardContent>
      </Card>

      <DiscardDraftDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        title={t("supportDocs.detail.discard_title", { documentId: doc.documentId })}
        description={t("supportDocs.detail.discard_body")}
        pending={discard.isPending}
        onConfirm={() => void onDiscard()}
      />
    </div>
  );
}

function Row({
  label,
  value,
  bold,
}: {
  label: string;
  value: string;
  bold?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between ${bold ? "font-semibold" : ""}`}
    >
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}
